import { Box } from '@lifeomic/chroma-react/components/Box';
import { ButtonLink } from '@lifeomic/chroma-react/components/ButtonLink';
import { defineMessages, useIntl } from 'react-intl';
import { Divider } from '@lifeomic/chroma-react/components/Divider';
import { FormBox } from '@lifeomic/chroma-react/components/FormBox';
import { Radio, RadioGroup } from '@lifeomic/chroma-react/components/Radio';
import {
  Actions,
  SlideOver,
  Body,
  Header,
  HeaderProps,
  SlideOverProps,
} from '@lifeomic/chroma-react/components/SlideOver';
import { TextArea } from '@lifeomic/chroma-react/components/TextArea';
import { TextField } from '@lifeomic/chroma-react/components/TextField';
import { Toggle } from '@lifeomic/chroma-react/components/Toggle';
import { useABAC } from '@lifeomic/phc-web-toolkit/src/hooks/useABAC';
import { useActiveProject } from '@lifeomic/phc-web-toolkit/src/hooks/useActiveProject';
import * as React from 'react';
import sharedMessages from '../data/createProjectMessages';
import urls from '../../../../util/urls';
import { Settings } from '@lifeomic/chromicons';

const messages = defineMessages({
  title: {
    id: 'projects.landingPage.settingsSlideOver.title',
    defaultMessage: 'Project Settings',
  },
  editSettings: {
    id: 'projects.landingPage.settingsSlideOver.editSettings',
    defaultMessage: 'Edit Settings',
  },
});

interface SettingsSlideOver {
  isOpen: SlideOverProps['isOpen'];
  onClose?: HeaderProps['onClose'];
}

export const SettingsSlideOver: React.FC<SettingsSlideOver> = ({
  isOpen,
  onClose,
}) => {
  const { formatMessage } = useIntl();

  const [activeProject] = useActiveProject();
  const [canPerform, permissions] = useABAC();

  return (
    <SlideOver isOpen={isOpen}>
      <Header
        title={formatMessage(messages.title)}
        titleIcon={Settings}
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        onClose={onClose}
      />
      <Body as={FormBox} fullWidth fullHeight>
        <TextField
          label={formatMessage(sharedMessages.name)}
          name="name"
          value={activeProject?.name}
          readOnly
          fullWidth
        />
        <TextArea
          label={formatMessage(sharedMessages.description)}
          name="description"
          value={activeProject?.description}
          readOnly
          fullWidth
        />
        <TextField
          label={formatMessage(sharedMessages.owner)}
          secondaryLabel={formatMessage(sharedMessages.ownerSecondary)}
          name="owner"
          type="email"
          value={activeProject?.owner}
          readOnly
          fullWidth
        />
        <TextField
          label={formatMessage(sharedMessages.shortNameLabel)}
          name="shortName"
          readOnly
          fullWidth
        />
        <Divider />
        <Box direction="column">
          <RadioGroup
            title={formatMessage(sharedMessages.genomeBuildSimple)}
            value={activeProject?.genomeBuild}
          >
            <Radio
              value="GRCh37"
              label={formatMessage(sharedMessages.genomeBuildGRCh37)}
              disabled
            />
            <Radio
              value="GRCh38"
              label={formatMessage(sharedMessages.genomeBuildGRCh38)}
              disabled
            />
          </RadioGroup>
        </Box>
        <Divider />
        <Toggle
          label={formatMessage(sharedMessages.auditLogging)}
          checked={Boolean(activeProject?.auditLogging)}
          disabled
        />
      </Body>
      {canPerform(permissions.projectAdmin) && (
        <Actions>
          <ButtonLink
            to={urls.app.projectSettings(activeProject?.id)}
            fullWidth
          >
            {formatMessage(messages.editSettings)}
          </ButtonLink>
        </Actions>
      )}
    </SlideOver>
  );
};
